if (!customElements.get('bundle-variant-picker')) {
  class BundleVariantPicker extends HTMLElement {
    constructor() {
      super();

      this.SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', '5XL'];

      this.bundleData = null;
      this.selections = {
        globalSize: null,
        globalColor: {},
        overrides: {}
      };
    }

    connectedCallback() {
      const form = this.closest('bundle-product-form');
      if (!form) return;

      const jsonScript = form.querySelector('[data-bundle-products]');
      if (!jsonScript) return;

      try {
        this.bundleData = JSON.parse(jsonScript.textContent);
      } catch (e) {
        console.error('[BundleVariantPicker] Failed to parse bundle data', e);
        return;
      }

      this.productFormId = this.dataset.productFormId || '';
      this.swatchBorderRadius = this.dataset.swatchBorderRadius || '50';
      this.processData();
      this.render();
      this.applyUnavailableState();
    }

    formatOptionLabel(optionName, setName) {
      if (!setName || setName === 'Default' || setName === 'default') return optionName;
      return optionName + ' (' + setName + ')';
    }

    buildNativeFieldset(optionName, isColor, idPrefix) {
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'product-form__controls' + (isColor ? ' product-form__controls--colors' : '');
      if (idPrefix) fieldset.id = idPrefix;

      const groupWrapper = document.createElement('div');
      groupWrapper.className = 'product-form__group-wrapper';

      const legend = document.createElement('legend');
      legend.className = 'product-form__group-name';
      legend.textContent = optionName;
      groupWrapper.appendChild(legend);

      const controlsWrap = document.createElement('div');
      controlsWrap.className = 'product-form__controls-group-wrapper';

      const group = document.createElement('div');
      group.className = 'product-form__controls-group';

      controlsWrap.appendChild(group);
      groupWrapper.appendChild(controlsWrap);
      fieldset.appendChild(groupWrapper);

      return { fieldset, group };
    }

    applyUnavailableState() {
      const form = this.closest('bundle-product-form');
      if (!form) return;

      const submitBtn = form.querySelector('button[type="submit"][name="add"]');
      if (this.bundleUnavailable) {
        if (submitBtn) {
          submitBtn.setAttribute('disabled', 'disabled');
          submitBtn.classList.add('bundle-form__submit--unavailable');
          const span = submitBtn.querySelector('span');
          if (span) span.textContent = this.t('cannot_add_bundle');
        }
        form.setAttribute('data-bundle-unavailable', 'true');
      } else {
        if (submitBtn) {
          submitBtn.removeAttribute('disabled');
          submitBtn.classList.remove('bundle-form__submit--unavailable');
        }
        form.removeAttribute('data-bundle-unavailable');
      }
    }

    processData() {
      const products = this.bundleData.products;

      this.unavailableProducts = [];
      products.forEach(product => {
        const anyAvailable = (product.variants || []).some(v => v.available);
        if (!anyAvailable) this.unavailableProducts.push(product);

        (product.options || []).forEach(opt => {
          const pos = (opt.position || 1) - 1;
          (opt.values || []).forEach(valObj => {
            const value = valObj.value || valObj;
            const hasAvail = (product.variants || []).some(v => v.available && v.options && v.options[pos] === value);
            valObj.unavailable = !hasAvail;
          });
        });
      });

      this.bundleUnavailable = this.unavailableProducts.length > 0;

      this.expandedItems = [];
      products.forEach((product, productIndex) => {
        const qty = product.quantity || 1;
        for (let q = 0; q < qty; q++) {
          this.expandedItems.push({
            ...product,
            itemIndex: this.expandedItems.length,
            productIndex,
            copyIndex: q
          });
        }
      });

      this.sets = {};
      this.expandedItems.forEach(item => {
        const setName = item.setName || 'Default';
        if (!this.sets[setName]) this.sets[setName] = [];
        this.sets[setName].push(item);
      });

      const allSizeOptions = products.map(p => {
        const sizeOpt = (p.options || []).find(o => o.isSize);
        return sizeOpt ? sizeOpt.values.map(v => v.value) : null;
      });

      const firstSizes = allSizeOptions.find(s => s !== null);
      let allMatch = true;
      allSizeOptions.forEach(sizes => {
        if (!sizes || JSON.stringify(sizes) !== JSON.stringify(firstSizes)) {
          allMatch = false;
        }
      });

      if (firstSizes && allMatch) {
        this.globalSizes = this.sortSizes(firstSizes);
        this.productSpecificSizes = [];
      } else {
        this.globalSizes = null;
        this.productSpecificSizes = products.filter(p => (p.options || []).find(o => o.isSize));
      }
    }

    sortSizes(sizes) {
      return [...sizes].sort((a, b) => {
        const ai = this.SIZE_ORDER.indexOf(a.toUpperCase());
        const bi = this.SIZE_ORDER.indexOf(b.toUpperCase());
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
        const an = parseFloat(a);
        const bn = parseFloat(b);
        if (!isNaN(an) && !isNaN(bn)) return an - bn;
        return a.localeCompare(b);
      });
    }

    render() {
      this.renderSizePickers();
      this.renderColorPickers();
      this.renderCustomizeSection();
      this.updateSummary();
    }

    renderSizePickers() {
      const container = this.querySelector('[data-sizes-container]');
      if (!container) return;

      container.innerHTML = '';

      if (this.globalSizes) {
        const firstSizeProduct = this.bundleData.products.find(p => (p.options || []).find(o => o.isSize));
        const sizeOptName = firstSizeProduct ? (firstSizeProduct.options.find(o => o.isSize) || {}).name : 'Taille';

        const { fieldset, group } = this.buildNativeFieldset(this.formatOptionLabel(sizeOptName, null), false, 'BundleGlobalSize');

        const globalUnavailable = (size) => {
          return this.bundleData.products.some(p => {
            const opt = (p.options || []).find(o => o.isSize);
            if (!opt) return false;
            const v = (opt.values || []).find(vv => (vv.value || vv) === size);
            return v && v.unavailable;
          });
        };

        let firstAvailIdx = -1;
        this.globalSizes.forEach((size, idx) => {
          const ua = globalUnavailable(size);
          if (!ua && firstAvailIdx === -1) firstAvailIdx = idx;
        });

        this.globalSizes.forEach((size, idx) => {
          const ua = globalUnavailable(size);
          const { input, label } = this.createSizeOption('global', 0, 0, idx, size, idx === firstAvailIdx, 'bundle-global-size', ua);
          group.appendChild(input);
          group.appendChild(label);
        });

        if (this.globalSizes.length > 0) {
          this.selections.globalSize = firstAvailIdx >= 0 ? this.globalSizes[firstAvailIdx] : this.globalSizes[0];
        }

        container.appendChild(fieldset);
        container.addEventListener('change', this.onSizeChange.bind(this));
      } else if (this.productSpecificSizes.length > 0) {
        this.productSpecificSizes.forEach(product => {
          const sizeOpt = product.options.find(o => o.isSize);
          if (!sizeOpt || !sizeOpt.values || sizeOpt.values.length === 0) return;

          const sortedNames = this.sortSizes(sizeOpt.values.map(v => v.value));
          const valueByName = {};
          sizeOpt.values.forEach(v => { valueByName[v.value] = v; });

          const { fieldset, group } = this.buildNativeFieldset(this.formatOptionLabel(sizeOpt.name, product.setName), false, 'BundleSize-' + product.productId);

          let firstAvailIdx = sortedNames.findIndex(n => !(valueByName[n] && valueByName[n].unavailable));
          if (firstAvailIdx === -1) firstAvailIdx = 0;

          sortedNames.forEach((size, idx) => {
            const nameAttr = 'bundle-size-' + product.productId;
            const ua = valueByName[size] && valueByName[size].unavailable;
            const { input, label } = this.createSizeOption('prod-' + product.productId, 0, 0, idx, size, idx === firstAvailIdx, nameAttr, ua);
            group.appendChild(input);
            group.appendChild(label);
          });

          container.appendChild(fieldset);
        });

        container.addEventListener('change', this.onSizeChange.bind(this));
      }
    }

    renderColorPickers() {
      const container = this.querySelector('[data-colors-container]');
      if (!container) return;
      container.innerHTML = '';

      const setNames = Object.keys(this.sets);

      setNames.forEach(setName => {
        const itemsInSet = this.sets[setName];
        const firstColoredItem = itemsInSet.find(it => (it.options || []).some(o => o.isColor && (o.values || []).length > 0));
        if (!firstColoredItem) return;
        const colorOpt = firstColoredItem.options.find(o => o.isColor);
        if (!colorOpt || !colorOpt.values || colorOpt.values.length === 0) return;

        const { fieldset, group } = this.buildNativeFieldset(this.formatOptionLabel(colorOpt.name, setName), true, 'BundleColor-' + setName.replace(/\s+/g, '-').toLowerCase());

        const nameAttr = 'bundle-color-set-' + setName.replace(/\s+/g, '-').toLowerCase();

        const firstAvailIdx = colorOpt.values.findIndex(v => !v.unavailable);

        colorOpt.values.forEach((valObj, idx) => {
          const { input, label } = this.createItemColorOption(setName, 0, idx, valObj, idx === firstAvailIdx, nameAttr);
          group.appendChild(input);
          group.appendChild(label);
        });

        if (colorOpt.values.length > 0) {
          if (!this.selections.globalColor[setName]) {
            this.selections.globalColor[setName] = firstAvailIdx >= 0 ? colorOpt.values[firstAvailIdx].value : colorOpt.values[0].value;
          }
        }

        container.appendChild(fieldset);
      });

      container.addEventListener('change', this.onColorChange.bind(this));
    }

    hasRealOptions(item) {
      return (item.options || []).some(o => {
        if (o.name && o.name.toLowerCase() === 'title') {
          const vals = (o.values || []).map(v => (v.value || v));
          if (vals.length <= 1 && (vals[0] || '').toLowerCase() === 'default title') return false;
        }
        return (o.values || []).length > 1;
      });
    }

    renderCustomizeSection() {
      const container = this.querySelector('[data-customize-container]');
      if (!container) return;
      container.innerHTML = '';

      let rendered = 0;
      this.expandedItems.forEach((item, idx) => {
        if (!this.hasRealOptions(item)) return;
        const card = this.buildItemCard(item, idx);
        container.appendChild(card);
        rendered++;
      });

      const customizeDetails = container.closest('details');
      if (customizeDetails) customizeDetails.hidden = rendered <= 1;

      container.addEventListener('change', this.onItemOptionChange.bind(this));
      container.addEventListener('click', this.onResetClick.bind(this));

      this.updateCustomizeBadge();
    }

    buildItemCard(item, cardIndex) {
      const itemUnavailable = !(item.variants || []).some(v => v.available);

      const card = document.createElement('div');
      card.className = 'bundle-variant-picker__item-card' + (itemUnavailable ? ' bundle-variant-picker__item-card--unavailable' : '');
      card.dataset.cardIndex = cardIndex;

      const imgWrapper = document.createElement('div');
      if (item.featuredImage) {
        const img = document.createElement('img');
        img.src = item.featuredImage;
        img.alt = item.productTitle;
        img.className = 'bundle-variant-picker__item-image';
        img.loading = 'lazy';
        imgWrapper.appendChild(img);
      } else {
        const placeholder = document.createElement('div');
        placeholder.className = 'bundle-variant-picker__item-image-placeholder';
        imgWrapper.appendChild(placeholder);
      }
      card.appendChild(imgWrapper);

      const content = document.createElement('div');
      content.className = 'bundle-variant-picker__item-content';

      const header = document.createElement('div');
      header.className = 'bundle-variant-picker__item-header';

      const title = document.createElement('p');
      title.className = 'bundle-variant-picker__item-title';
      title.textContent = item.productTitle;
      header.appendChild(title);

      if (itemUnavailable) {
        const soldOutBadge = document.createElement('span');
        soldOutBadge.className = 'bundle-variant-picker__item-sold-out-badge';
        soldOutBadge.textContent = this.t('sold_out');
        header.appendChild(soldOutBadge);
      }

      const customizedBadge = document.createElement('span');
      customizedBadge.className = 'bundle-variant-picker__item-customized-badge';
      customizedBadge.dataset.customizedBadge = cardIndex;
      customizedBadge.hidden = true;
      customizedBadge.textContent = this.t('customized');
      header.appendChild(customizedBadge);

      content.appendChild(header);

      const subRow = document.createElement('div');
      subRow.className = 'bundle-variant-picker__item-sub-row';

      const selectionText = document.createElement('p');
      selectionText.className = 'bundle-variant-picker__item-selection-text';
      selectionText.dataset.selectionText = cardIndex;
      subRow.appendChild(selectionText);

      const resetBtn = document.createElement('button');
      resetBtn.type = 'button';
      resetBtn.className = 'bundle-variant-picker__reset-button';
      resetBtn.dataset.resetCard = cardIndex;
      resetBtn.hidden = true;
      resetBtn.textContent = this.t('reset');
      subRow.appendChild(resetBtn);

      content.appendChild(subRow);

      const pickerArea = document.createElement('div');
      pickerArea.className = 'bundle-variant-picker__item-pickers';
      pickerArea.dataset.pickerArea = cardIndex;

      const realOptions = (item.options || []).filter(o => {
        if (o.name && o.name.toLowerCase() === 'title') {
          const vals = (o.values || []).map(v => (v.value || v));
          if (vals.length <= 1 && (vals[0] || '').toLowerCase() === 'default title') return false;
        }
        return (o.values || []).length > 1 || (o.values || []).length > 0 && (o.values[0].value || o.values[0]) !== 'Default Title';
      });

      realOptions.forEach((optObj, optIdx) => {
        const { fieldset, group } = this.buildNativeFieldset(this.formatOptionLabel(optObj.name, item.setName), !!optObj.isColor, 'BundleItem-' + cardIndex + '-' + (optObj.position || optIdx));

        const nameAttr = 'bundle-item-' + cardIndex + '-' + optObj.name.replace(/\s+/g, '-').toLowerCase();

        if (optObj.isColor) {
          const firstAvailIdx = optObj.values.findIndex(v => !v.unavailable);
          optObj.values.forEach((valObj, vIdx) => {
            const { input, label } = this.createItemColorOption(cardIndex, optIdx, vIdx, valObj, vIdx === firstAvailIdx, nameAttr);
            input.dataset.cardIndex = cardIndex;
            input.dataset.optionName = optObj.name;
            input.dataset.isOverride = 'true';
            group.appendChild(input);
            group.appendChild(label);
          });
        } else {
          const valueByName = {};
          optObj.values.forEach(v => { valueByName[v.value] = v; });
          const sortedNames = optObj.isSize
            ? this.sortSizes(optObj.values.map(v => v.value))
            : optObj.values.map(v => v.value);

          let firstAvailIdx = sortedNames.findIndex(n => !(valueByName[n] && valueByName[n].unavailable));
          if (firstAvailIdx === -1) firstAvailIdx = 0;

          sortedNames.forEach((name, vIdx) => {
            const valObj = valueByName[name] || { value: name };
            const { input, label } = this.createItemSizeOption(cardIndex, optIdx, vIdx, valObj, vIdx === firstAvailIdx, nameAttr);
            input.dataset.cardIndex = cardIndex;
            input.dataset.optionName = optObj.name;
            input.dataset.isOverride = 'true';
            group.appendChild(input);
            group.appendChild(label);
          });
        }

        pickerArea.appendChild(fieldset);
      });

      content.appendChild(pickerArea);

      card.appendChild(content);

      this.updateItemSelectionText(cardIndex);

      return card;
    }

    createSizeOption(row, pos, _unused, idx, value, checked, nameAttr, unavailable) {
      const id = 'bundle-' + row + '-' + pos + '-' + idx;
      const input = document.createElement('input');
      input.type = 'radio';
      input.id = id;
      input.name = nameAttr;
      input.value = value;
      if (this.productFormId) input.setAttribute('form', this.productFormId);
      if (checked && !unavailable) input.checked = true;
      if (unavailable) input.className = 'disabled';

      const label = document.createElement('label');
      label.htmlFor = id;
      label.className = 'focus-inset';
      label.appendChild(document.createTextNode(value));

      const hiddenTxt = document.createElement('span');
      hiddenTxt.className = 'visually-hidden';
      hiddenTxt.textContent = window.bundleI18n && window.bundleI18n.variant_sold_out_or_unavailable
        ? window.bundleI18n.variant_sold_out_or_unavailable
        : 'Variant sold out or unavailable';
      label.appendChild(hiddenTxt);

      return { input, label };
    }

    createItemColorOption(row, pos, idx, valObj, checked, nameAttr) {
      const value = valObj.value || valObj;
      const swatchStyle = (valObj.swatchStyle) || '';
      const isWhite = valObj.isWhite || false;
      const unavailable = valObj.unavailable || false;

      const id = 'bundle-' + row + '-' + pos + '-' + idx;
      const input = document.createElement('input');
      input.type = 'radio';
      input.id = id;
      input.name = nameAttr;
      input.value = value;
      if (this.productFormId) input.setAttribute('form', this.productFormId);
      if (checked && !unavailable) input.checked = true;
      if (unavailable) input.className = 'disabled';

      const isBlack = valObj.isBlack || (swatchStyle === 'rgb(0,0,0)' || swatchStyle === 'black' || value.toLowerCase() === 'black' || value.toLowerCase() === 'noir');

      const label = document.createElement('label');
      label.htmlFor = id;
      label.className = 'color-swatch focus-inset' + (isWhite ? ' color-white' : '') + (isBlack ? ' color-black' : '');
      label.setAttribute('aria-label', value);
      const radius = this.swatchBorderRadius || '50';
      let styleStr = 'border-radius: ' + radius + 'px;';
      if (swatchStyle) {
        styleStr += ' --swatch-color: ' + swatchStyle + ';';
      }
      label.setAttribute('style', styleStr);

      return { input, label };
    }

    createItemSizeOption(row, pos, idx, valObj, checked, nameAttr) {
      const value = valObj.value || valObj;
      const unavailable = (valObj && typeof valObj === 'object') ? (valObj.unavailable || false) : false;
      return this.createSizeOption(row, pos, pos, idx, value, checked, nameAttr, unavailable);
    }

    onSizeChange(event) {
      const input = event.target;
      if (input.type !== 'radio') return;

      if (input.name.startsWith('bundle-global-size')) {
        this.selections.globalSize = input.value;
      } else if (input.name.startsWith('bundle-size-')) {
        const productId = parseInt(input.name.replace('bundle-size-', ''));
        if (!this.selections.productSizes) this.selections.productSizes = {};
        this.selections.productSizes[productId] = input.value;
      }

      this.expandedItems.forEach((_item, cardIndex) => {
        if (!this.selections.overrides[cardIndex]) {
          this.updateItemSelectionText(cardIndex);
        }
      });

      this.syncCustomizeSectionDefaults();
      this.updateSummary();
      this.dispatchSelectionChange();
    }

    onColorChange(event) {
      const input = event.target;
      if (input.type !== 'radio') return;

      const match = input.name.match(/^bundle-color-set-(.+)$/);
      if (match) {
        const setName = Object.keys(this.sets).find(s =>
          s.replace(/\s+/g, '-').toLowerCase() === match[1]
        );
        if (setName) {
          this.selections.globalColor[setName] = input.value;
        }
      }

      this.expandedItems.forEach((_item, cardIndex) => {
        if (!this.selections.overrides[cardIndex]) {
          this.updateItemSelectionText(cardIndex);
        }
      });

      this.syncCustomizeSectionDefaults();
      this.updateSummary();
      this.dispatchSelectionChange();
    }

    onItemOptionChange(event) {
      const input = event.target;
      if (input.type !== 'radio' || input.dataset.isOverride !== 'true') return;

      const cardIndex = parseInt(input.dataset.cardIndex);
      const optionName = input.dataset.optionName;

      if (!this.selections.overrides[cardIndex]) {
        this.selections.overrides[cardIndex] = {};
      }
      this.selections.overrides[cardIndex][optionName] = input.value;

      this.updateItemSelectionText(cardIndex);
      this.updateItemCustomizedState(cardIndex);
      this.updateCustomizeBadge();
      this.updateSummary();
      this.dispatchSelectionChange();
    }

    onResetClick(event) {
      const btn = event.target.closest('[data-reset-card]');
      if (!btn) return;

      const cardIndex = parseInt(btn.dataset.resetCard);
      delete this.selections.overrides[cardIndex];

      this.updateItemSelectionText(cardIndex);
      this.updateItemCustomizedState(cardIndex);
      this.updateCustomizeBadge();
      this.syncCustomizeSectionDefaults();
      this.updateSummary();
      this.dispatchSelectionChange();
    }

    syncCustomizeSectionDefaults() {
      this.expandedItems.forEach((item, cardIndex) => {
        if (this.selections.overrides[cardIndex]) return;

        const pickerArea = this.querySelector('[data-picker-area="' + cardIndex + '"]');
        if (!pickerArea) return;

        (item.options || []).forEach(optObj => {
          const defaultVal = this.getDefaultOptionValue(item, optObj);
          if (!defaultVal) return;

          const nameAttr = 'bundle-item-' + cardIndex + '-' + optObj.name.replace(/\s+/g, '-').toLowerCase();
          const input = pickerArea.querySelector('input[name="' + nameAttr + '"][value="' + CSS.escape(defaultVal) + '"]');
          if (input) input.checked = true;
        });
      });
    }

    getDefaultOptionValue(item, optObj) {
      if (optObj.isSize) {
        if (this.globalSizes) return this.selections.globalSize;
        if (this.selections.productSizes) return this.selections.productSizes[item.productId];
        return null;
      }
      if (optObj.isColor) {
        const setName = item.setName || 'Default';
        return this.selections.globalColor[setName] || null;
      }
      return null;
    }

    updateItemSelectionText(cardIndex) {
      const el = this.querySelector('[data-selection-text="' + cardIndex + '"]');
      if (!el) return;

      const item = this.expandedItems[cardIndex];
      const variant = this.resolveVariant(cardIndex);
      if (variant) {
        el.textContent = variant.title;
      } else {
        el.textContent = '';
      }
    }

    updateItemCustomizedState(cardIndex) {
      const badge = this.querySelector('[data-customized-badge="' + cardIndex + '"]');
      const resetBtn = this.querySelector('[data-reset-card="' + cardIndex + '"]');
      const hasOverride = !!this.selections.overrides[cardIndex];

      if (badge) badge.hidden = !hasOverride;
      if (resetBtn) resetBtn.hidden = !hasOverride;
    }

    updateCustomizeBadge() {
      const badge = this.querySelector('[data-customize-count]');
      if (!badge) return;

      const count = Object.keys(this.selections.overrides).length;
      if (count > 0) {
        badge.hidden = false;
        badge.textContent = count + ' ' + this.t('customized_count');
      } else {
        badge.hidden = true;
      }
    }

    updateSummary() {
      const container = this.querySelector('[data-summary-container]');
      if (!container) return;

      container.innerHTML = '';

      this.expandedItems.forEach((item, cardIndex) => {
        const variant = this.resolveVariant(cardIndex);
        const li = document.createElement('li');
        li.className = 'bundle-variant-picker__summary-item';

        const name = document.createElement('span');
        name.className = 'bundle-variant-picker__summary-product';
        name.textContent = item.productTitle;
        li.appendChild(name);

        const hasRealVariants = this.hasRealOptions(item);
        if (hasRealVariants) {
          const variantText = document.createElement('span');
          variantText.className = 'bundle-variant-picker__summary-variant';
          variantText.textContent = variant ? variant.title : '—';
          li.appendChild(variantText);
        }

        container.appendChild(li);
      });
    }

    resolveVariant(cardIndex) {
      const item = this.expandedItems[cardIndex];
      if (!item || !item.variants || item.variants.length === 0) return null;

      const override = this.selections.overrides[cardIndex] || {};

      const resolvedOptions = {};
      (item.options || []).forEach(optObj => {
        if (override[optObj.name] !== undefined) {
          resolvedOptions[optObj.name] = override[optObj.name];
        } else {
          resolvedOptions[optObj.name] = this.getDefaultOptionValue(item, optObj);
        }
      });

      const match = item.variants.find(v => {
        if (!v.options || v.options.length === 0) return true;
        return (item.options || []).every((optObj, i) => {
          const desired = resolvedOptions[optObj.name];
          if (!desired) return true;
          return v.options[i] && v.options[i].toLowerCase() === desired.toLowerCase();
        });
      });

      if (match) return match;

      return item.variants.find(v => v.available) || item.variants[0] || null;
    }

    getSelectedVariants() {
      const bundleId = this.bundleData.bundleId;
      const bundleHandle = this.bundleData.bundleHandle;

      return this.expandedItems.map((_item, cardIndex) => {
        const variant = this.resolveVariant(cardIndex);
        return {
          id: variant ? variant.id : null,
          quantity: 1,
          properties: {
            _bundle_id: bundleId,
            _bundle_handle: bundleHandle
          }
        };
      }).filter(v => v.id !== null);
    }

    dispatchSelectionChange() {
      this.dispatchEvent(new CustomEvent('bundle:selection-change', {
        bubbles: true,
        detail: { selections: this.selections }
      }));
    }

    t(key, interpolation) {
      const map = {
        size_for_all: window.bundleI18n && window.bundleI18n.size_for_all ? window.bundleI18n.size_for_all : 'Size for all items',
        size_for_product: window.bundleI18n && window.bundleI18n.size_for_product ? window.bundleI18n.size_for_product : 'Size for __PRODUCT_NAME__',
        color_for: window.bundleI18n && window.bundleI18n.color_for ? window.bundleI18n.color_for : 'Color for',
        customized: window.bundleI18n && window.bundleI18n.customized ? window.bundleI18n.customized : 'Customized',
        customized_count: window.bundleI18n && window.bundleI18n.customized_count ? window.bundleI18n.customized_count : 'customized',
        your_selection: window.bundleI18n && window.bundleI18n.your_selection ? window.bundleI18n.your_selection : 'Your Selection',
        reset: window.bundleI18n && window.bundleI18n.reset ? window.bundleI18n.reset : 'Reset',
        cannot_add_bundle: window.bundleI18n && window.bundleI18n.cannot_add_bundle ? window.bundleI18n.cannot_add_bundle : 'Cannot add bundle',
        sold_out: window.bundleI18n && window.bundleI18n.sold_out ? window.bundleI18n.sold_out : 'Sold out'
      };
      let str = map[key] || key;
      if (key === 'size_for_product' && interpolation) {
        str = str.replace('__PRODUCT_NAME__', interpolation);
      }
      return str;
    }
  }

  customElements.define('bundle-variant-picker', BundleVariantPicker);
}
